import { API } from "../../../services/api";
import { clearDraftBackup, saveDraftBackup } from "../../save/draftBackup";
import { LOCK_LOST, SAVE_REQUEST_CONFIG } from "../model/commandeConstants";
import { cloneDraft, transformToBackend } from "../model/commandeDraft";
import { attachPatientClientKeys, findMatchingPatient, samePharmaPatientPayload } from "../model/commandeDraftMerge";
import { computeProductionCommentPatches, computeProduitPatches } from "../model/commandePatches";
import {
    apiErrorMessage,
    canDeferPatientSave,
    hasIncompletePatientIdentity,
    isLockLostError,
    patientLabel,
    patientsBlockedByDestination,
    validatePatients,
} from "../model/commandeValidation";

// Moteur de sauvegarde d'une commande. Chaque étape reçoit le contexte de la page (`ctx`) : les refs tenues à jour
// à chaque saisie (brouillon, version serveur, sauvegarde en cours) et les setters d'état de la page.

const LOCK_FAILURE = "Sauvegarde impossible : le verrou de la commande n'a pas pu être acquis.";

// Remplace le brouillon courant et le marque à sauvegarder.
function replaceDirtyDraft(ctx, nextDraft) {
    ctx.draftRef.current = nextDraft;
    ctx.setDraft(nextDraft);
    ctx.isDirtyRef.current = true;
    ctx.setIsDirty(true);
}

// Une sauvegarde automatique ne reprend pas un verrou relâché ; une sauvegarde demandée par l'utilisateur, si.
async function acquireLock(ctx, silent) {
    const acquired = await ctx.tryAcquire(ctx.draftRef.current.id, silent ? {} : { force: true });
    if (!acquired) {
        saveDraftBackup(ctx.draftRef.current.id, ctx.draftRef.current);
        ctx.setAutoSaveStatus("error");
        ctx.setSaveErrors([LOCK_FAILURE]);
        return false;
    }
    ctx.hasLockRef.current = true;
    return true;
}

// Brouillon gardé localement sans appel serveur : nom ou prénom manquant, ou médicament sans destination sur un
// patient ajouté ou modifié. Renvoie les erreurs à afficher, ou null si la sauvegarde peut partir.
function localOnlyErrors(ctx) {
    const draft = ctx.draftRef.current;
    if (computeProductionCommentPatches(draft, ctx.originalDraftRef.current) !== null || canDeferPatientSave(draft)) {
        return null;
    }
    if (hasIncompletePatientIdentity(draft.patients)) {
        return validatePatients(draft.patients || [])
            .filter(error => error.includes("nom obligatoire") || error.includes("prénom obligatoire"));
    }
    const patientsMissingDestination = patientsBlockedByDestination(draft.patients, ctx.originalDraftRef.current?.patients);
    if (patientsMissingDestination.length === 0) return null;
    const blockedLabels = new Set(patientsMissingDestination.map(patient =>
        patientLabel(patient, (draft.patients || []).indexOf(patient))));
    return validatePatients(draft.patients || [])
        .filter(error => error.includes("destination obligatoire"))
        .filter(error => blockedLabels.has(error.split(" : ")[0]));
}

// Fin d'une sauvegarde réussie : s'il y a eu une saisie pendant la requête, une sauvegarde de rattrapage suivra.
function finishSuccessfulSave(ctx, draftSnapshot, saveRevision) {
    const hasNewerChanges = ctx.draftRevisionRef.current !== saveRevision;
    if (hasNewerChanges) ctx.saveAgainRef.current = true;
    ctx.isDirtyRef.current = hasNewerChanges;
    ctx.setIsDirty(hasNewerChanges);
    if (hasNewerChanges) {
        ctx.setAutoSaveStatus("local");
    } else {
        ctx.optimisticListPatchesRef.current.delete(draftSnapshot.id);
        clearDraftBackup(draftSnapshot.id);
        ctx.setAutoSaveStatus("saved");
        ctx.resetSavedStatusLater();
    }
    return true;
}

// Réclamation dont les patients sont incomplets : seuls les champs commande sont enregistrés, les patients
// restent dans le brouillon local.
function finishDeferredPatientSave(ctx, commandeResponse, draftSnapshot, saveRevision) {
    ctx.syncSavedCommande(commandeResponse.data, draftSnapshot, ctx.draftRef.current);
    ctx.setSaveErrors([]);
    if (draftSnapshot.patients?.length) {
        saveDraftBackup(draftSnapshot.id, ctx.draftRef.current);
    }
    const hasNewerChanges = ctx.draftRevisionRef.current !== saveRevision;
    if (hasNewerChanges) ctx.saveAgainRef.current = true;
    ctx.isDirtyRef.current = hasNewerChanges;
    ctx.setIsDirty(hasNewerChanges);
    ctx.setAutoSaveStatus(hasNewerChanges ? "local" : "saved");
    if (!hasNewerChanges) ctx.resetSavedStatusLater();
    return true;
}

// Un HTTP 200 ne suffit pas : chaque ligne envoyée doit avoir une liaison patient-commande confirmée dans la
// relecture DB.
export function confirmPatientResults(patientItems, responseData) {
    const rawResults = responseData?.results || [];
    const persistedCommandePatientIds = new Set(
        (responseData?.commande?.patients || [])
            .map(patient => patient.commandePatientId)
            .filter(Boolean)
    );
    return patientItems.map(item => {
        const result = rawResults.find(candidate => candidate.clientKey === item.clientKey);
        if (!result) {
            return {
                clientKey: item.clientKey,
                patient: item.patient,
                saved: false,
                error: "Le serveur n'a pas confirmé la sauvegarde du patient.",
            };
        }
        const commandePatientId = result.patient?.commandePatientId;
        if (result.saved && (
            !commandePatientId
            || !persistedCommandePatientIds.has(commandePatientId)
        )) {
            return {
                ...result,
                saved: false,
                error: "La liaison du patient à la commande n'a pas été persistée.",
            };
        }
        return result;
    });
}

function buildPatientItems(ctx, draftSnapshot) {
    const originalPatients = ctx.originalDraftRef.current?.patients || [];
    return (draftSnapshot.patients || []).map(patient => {
        const originalPatient = findMatchingPatient(patient, originalPatients);
        return {
            clientKey: patient._clientKey,
            patient: transformToBackend({
                ...draftSnapshot,
                patients: [patient],
            }, ctx.defaultMedicamentDestination).patients[0],
            unchanged: samePharmaPatientPayload(patient, originalPatient),
        };
    });
}

// Conflit d'identité signalé dans une réponse partielle. La réponse contient encore la fiche telle qu'elle est
// enregistrée en base : la synchroniser remettait l'ancienne date dans le brouillon avant que l'utilisateur clique
// sur « Mettre à jour le patient ». Conserver la saisie en conflit permet au second envoi de transmettre réellement
// la nouvelle valeur.
function keepConflictingPatient(ctx, identityConflictResult, draftSnapshot, draftBeforePartialSync) {
    const patientIndex = (draftSnapshot.patients || []).findIndex(
        patient => patient._clientKey === identityConflictResult.clientKey
    );
    const unresolvedPatient = (draftBeforePartialSync?.patients || []).find(
        patient => patient._clientKey === identityConflictResult.clientKey
    ) || (draftSnapshot.patients || [])[patientIndex];
    if (unresolvedPatient) {
        replaceDirtyDraft(ctx, {
            ...ctx.draftRef.current,
            patients: (ctx.draftRef.current?.patients || []).map(patient =>
                patient._clientKey === identityConflictResult.clientKey
                    ? unresolvedPatient
                    : patient
            ),
        });
    }
    const conflict = {
        code: "PATIENT_IDENTITY_CONFLICT",
        message: identityConflictResult.error,
        ...identityConflictResult.conflict,
        patientIndex,
    };
    ctx.patientIdentityConflictRef.current = conflict;
    ctx.setPatientIdentityConflict(conflict);
    ctx.setSaveErrors([]);
    ctx.setAutoSaveStatus("local");
    ctx.saveAgainRef.current = false;
    const currentDraft = ctx.draftRef.current;
    if (currentDraft?.id) saveDraftBackup(currentDraft.id, currentDraft);
    return false;
}

// Patients refusés : ils restent dans le brouillon tels qu'envoyés, les autres prennent la version serveur.
function keepFailedPatients(ctx, failedResults, draftSnapshot) {
    const failedKeys = new Set(failedResults.map(result => result.clientKey));
    const restoredDraft = {
        ...ctx.draftRef.current,
        patients: (draftSnapshot.patients || []).map(patient => {
            if (failedKeys.has(patient._clientKey)) return patient;
            return (ctx.draftRef.current?.patients || []).find(candidate =>
                candidate._clientKey === patient._clientKey
            ) || patient;
        }),
    };
    const errors = failedResults.map(result => {
        const failedPatient = (draftSnapshot.patients || []).find(
            patient => patient._clientKey === result.clientKey
        );
        return `${patientLabel(failedPatient, 0)} : ${result.error || "sauvegarde impossible"}`;
    });
    replaceDirtyDraft(ctx, restoredDraft);
    saveDraftBackup(restoredDraft.id, restoredDraft);
    ctx.setSaveErrors(errors);
    ctx.setAutoSaveStatus("partial");
    return false;
}

function handleFailedPatients(ctx, failedResults, responseCommande, draftSnapshot) {
    const draftBeforePartialSync = ctx.draftRef.current;
    ctx.syncSavedCommande(responseCommande, draftSnapshot);
    const identityConflictResult = failedResults.find(result => result.conflict);
    if (identityConflictResult) {
        return keepConflictingPatient(ctx, identityConflictResult, draftSnapshot, draftBeforePartialSync);
    }
    return keepFailedPatients(ctx, failedResults, draftSnapshot);
}

// Les champs commande sont enregistrés séparément, puis chaque patient est traité dans sa propre transaction côté
// backend. Renvoie true/false quand la sauvegarde est terminée, undefined pour poursuivre vers la fin commune.
async function saveCommandeAndPatients(ctx, draftSnapshot, saveRevision) {
    const commandePayload = {
        ...transformToBackend(draftSnapshot, ctx.defaultMedicamentDestination),
        patients: null,
    };
    const commandeResponse = await API.patch(
        `/commandes/${draftSnapshot.id}/traitement`,
        commandePayload,
        SAVE_REQUEST_CONFIG
    );
    if (canDeferPatientSave(draftSnapshot)) {
        return finishDeferredPatientSave(ctx, commandeResponse, draftSnapshot, saveRevision);
    }
    const patientItems = buildPatientItems(ctx, draftSnapshot);
    const patientsResponse = await API.patch(
        `/commandes/${draftSnapshot.id}/patients`,
        {
            ehpadId: draftSnapshot.ehpadId,
            patients: patientItems,
        },
        SAVE_REQUEST_CONFIG
    );
    const results = confirmPatientResults(patientItems, patientsResponse.data);
    const failedResults = results.filter(result => !result.saved);
    const responseCommande = attachPatientClientKeys(patientsResponse.data?.commande, results);
    if (failedResults.length > 0) {
        return handleFailedPatients(ctx, failedResults, responseCommande, draftSnapshot);
    }
    ctx.syncSavedCommande(responseCommande, draftSnapshot);
    ctx.setSaveErrors([]);
    if (ctx.draftRevisionRef.current !== saveRevision) {
        const mergedDraft = ctx.draftRef.current;
        saveDraftBackup(mergedDraft.id, mergedDraft);
        ctx.saveAgainRef.current = true;
    }
    return undefined;
}

// Un clic manuel doit toujours atteindre le backend lorsque le dirty state a raté une modification. En revanche,
// les changements limités aux médicaments conservent leurs PATCH ciblés.
async function saveSnapshot(ctx, draftSnapshot, saveRevision, force) {
    const computedPatches = computeProduitPatches(draftSnapshot, ctx.originalDraftRef.current);
    const patches = force && Array.isArray(computedPatches) && computedPatches.length === 0 ? null : computedPatches;
    const productionCommentPatches = computeProductionCommentPatches(draftSnapshot, ctx.originalDraftRef.current);
    if (Array.isArray(productionCommentPatches)) {
        await Promise.all(productionCommentPatches.map(({ id, patch }) =>
            API.patch(`/commande-patients/${id}/production-comments`, patch, SAVE_REQUEST_CONFIG)
        ));
        ctx.originalDraftRef.current = draftSnapshot;
        ctx.setSaveErrors([]);
    } else if (patches === null) {
        const finished = await saveCommandeAndPatients(ctx, draftSnapshot, saveRevision);
        if (finished !== undefined) return finished;
    } else {
        // Patches par produit uniquement
        await Promise.all(patches.map(({ id, patch }) => API.patch(`/medicaments/${id}`, patch, SAVE_REQUEST_CONFIG)));
        ctx.originalDraftRef.current = draftSnapshot;
        ctx.setSaveErrors([]);
    }
    return finishSuccessfulSave(ctx, draftSnapshot, saveRevision);
}

// 409 PATIENT_IDENTITY_CONFLICT : même date de naissance, le patient existant est mis à jour sans question ;
// sinon l'utilisateur choisit entre mettre à jour le patient existant et créer un homonyme.
function handleIdentityConflictResponse(ctx, conflictData) {
    const normalize = value => String(value ?? "").trim().toLocaleUpperCase("fr");
    const patientIndex = (ctx.draftRef.current?.patients || []).findIndex(patient =>
        patient.id === conflictData.existingPatientId
        || (
            normalize(patient.nom) === normalize(conflictData.nom)
            && normalize(patient.prenom) === normalize(conflictData.prenom)
            && (patient.dateNaissance || null) === (conflictData.requestedDateNaissance || null)
        )
    );
    const conflict = { ...conflictData, patientIndex };
    const sameBirthDate = (conflictData.existingDateNaissance || null) === (conflictData.requestedDateNaissance || null);
    if (sameBirthDate && patientIndex >= 0) {
        const nextDraft = {
            ...ctx.draftRef.current,
            patients: (ctx.draftRef.current.patients || []).map((patient, index) =>
                index === patientIndex
                    ? { ...patient, _identityResolution: "UPDATE_EXISTING", _identityCandidateId: conflictData.existingPatientId }
                    : patient
            ),
        };
        ctx.draftRevisionRef.current += 1;
        replaceDirtyDraft(ctx, nextDraft);
        ctx.setAutoSaveStatus("local");
        saveDraftBackup(nextDraft.id, nextDraft);
        ctx.saveAgainRef.current = true;
        return false;
    }
    ctx.patientIdentityConflictRef.current = conflict;
    ctx.setPatientIdentityConflict(conflict);
    ctx.setAutoSaveStatus("local");
    ctx.setSaveErrors([]);
    ctx.saveAgainRef.current = false;
    return false;
}

function handleSaveError(ctx, error, lockRetried) {
    if (error.response?.status === 409 && error.response?.data?.code === "PATIENT_IDENTITY_CONFLICT") {
        return handleIdentityConflictResponse(ctx, error.response.data);
    }
    if (!lockRetried && isLockLostError(error)) {
        // Le serveur ne trouve plus notre verrou : runSave le reprend et relance une fois.
        ctx.hasLockRef.current = false;
        return LOCK_LOST;
    }
    console.error("Save failed", error);
    ctx.rememberDraftForList();
    if (ctx.draftRef.current?.id) saveDraftBackup(ctx.draftRef.current.id, ctx.draftRef.current);
    ctx.setAutoSaveStatus("error");
    ctx.setSaveErrors([`Sauvegarde impossible : ${apiErrorMessage(error)}`]);
    return false;
}

// Une seule sauvegarde à la fois : une demande pendant une sauvegarde en cours est rejouée juste après.
export async function runSaveOnce(ctx, { silent = false, force = false, lockRetried = false } = {}) {
    if (!silent) ctx.restoredBackupRef.current = false;
    if (ctx.patientIdentityConflictRef.current) return false;
    if (!ctx.draftRef.current?.id) return false;
    if (!ctx.isDirtyRef.current && !force) return true;
    // Verrou déjà tenu : aucune attente, les contrôles suivants s'appliquent dans le même tour que la saisie.
    if (!ctx.hasLockRef.current && !await acquireLock(ctx, silent)) return false;
    const errors = localOnlyErrors(ctx);
    if (errors) {
        saveDraftBackup(ctx.draftRef.current.id, ctx.draftRef.current);
        ctx.setSaveErrors(errors);
        ctx.setAutoSaveStatus("local");
        return false;
    }
    if (ctx.savingRef.current) {
        ctx.saveAgainRef.current = true;
        return ctx.currentSavePromiseRef.current || false;
    }
    ctx.savingRef.current = true;
    ctx.setAutoSaveStatus("saving");
    const savePromise = saveSnapshot(ctx, cloneDraft(ctx.draftRef.current), ctx.draftRevisionRef.current, force);
    ctx.currentSavePromiseRef.current = savePromise;
    try {
        return await savePromise;
    } catch (e) {
        return handleSaveError(ctx, e, lockRetried);
    } finally {
        ctx.savingRef.current = false;
        ctx.currentSavePromiseRef.current = null;
        if (ctx.saveAgainRef.current) {
            ctx.saveAgainRef.current = false;
            if (ctx.isDirtyRef.current) {
                setTimeout(() => ctx.runSaveRef.current({ validate: true, silent: true }), 0);
            }
        }
    }
}

// Le verrou peut avoir été perdu côté serveur sans que la page le sache (relâché pour inactivité, expiré, pris
// dans un autre onglet puis relâché) : la sauvegarde le reprend alors et réessaie une fois.
export async function runSave(ctx, options = {}) {
    const result = await runSaveOnce(ctx, options);
    if (result !== LOCK_LOST) return result;
    const commandeId = ctx.draftRef.current?.id;
    const reacquired = commandeId ? await ctx.tryAcquire(commandeId, { force: true }) : false;
    if (reacquired) {
        ctx.hasLockRef.current = true;
        return runSaveOnce(ctx, { ...options, force: true, lockRetried: true });
    }
    if (commandeId) saveDraftBackup(commandeId, ctx.draftRef.current);
    ctx.setAutoSaveStatus("error");
    ctx.setSaveErrors([LOCK_FAILURE]);
    return false;
}

// Choix de l'utilisateur sur un conflit d'identité : la ligne patient est modifiée puis la sauvegarde relancée.
export function resolveIdentityConflict(ctx, patchPatient, { clearErrors = false } = {}) {
    const conflict = ctx.patientIdentityConflictRef.current;
    if (!conflict || conflict.patientIndex < 0 || !ctx.draftRef.current) return;
    const nextDraft = {
        ...ctx.draftRef.current,
        patients: (ctx.draftRef.current.patients || []).map((patient, index) =>
            index === conflict.patientIndex ? patchPatient(patient, conflict) : patient
        ),
    };
    ctx.patientIdentityConflictRef.current = null;
    ctx.setPatientIdentityConflict(null);
    ctx.draftRevisionRef.current += 1;
    replaceDirtyDraft(ctx, nextDraft);
    if (clearErrors) ctx.setSaveErrors([]);
    ctx.setAutoSaveStatus("local");
    saveDraftBackup(nextDraft.id, nextDraft);
    setTimeout(() => ctx.runSaveRef.current({ validate: true, silent: false }), 0);
}
